// Email drip sequences (audit #11 extension — moves beyond one-off
// send to recurring scheduled drips). Two-tab UI: Sequences (editor)
// + Active Enrollments (live queue with pause/resume/cancel).
//
// Step editor lets the operator define delay_days + subject + body
// per step. Merge tokens supported: {first_name}, {agent_name},
// {company} (rendered by /api/email/drips/tick).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Plus, Trash2, Pause, Play, X, Send, Calendar, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const TRIGGER_KINDS = [
  'manual','welcome','client_30d','client_90d','client_180d','client_1yr',
  'renewal_30d','renewal_60d','birthday','aep_warmup','oep_warmup',
  'cross_sell','win_back','custom',
];

const TEMPLATES = {
  welcome: {
    name: 'Welcome series',
    description: 'New client onboarding — day 0, day 3, day 14.',
    trigger_kind: 'welcome',
    steps: [
      { delay_days: 0, subject: 'Welcome to {company}, {first_name}!', body: 'Hi {first_name},\n\nThanks for trusting us with your coverage. I\'m {agent_name} and I\'ll be your point of contact. Save my number — text or call anytime.\n\n— {agent_name}' },
      { delay_days: 3, subject: 'Quick check-in', body: 'Hi {first_name}, just making sure your new coverage feels right. Any questions about your benefits, deductible, or how to use your plan?' },
      { delay_days: 14, subject: 'A favor + heads up', body: 'Hi {first_name},\n\nIf the coverage has been good, would you mind dropping a quick review? (Optional — no pressure.)\n\nAlso: heads up that renewal time will sneak up. I\'ll reach out 60 days before to walk you through options.\n\n— {agent_name}' },
    ],
  },
  renewal_30d: {
    name: 'Renewal — 30 days out',
    description: 'Fires 30 days before policy renewal date.',
    trigger_kind: 'renewal_30d',
    steps: [
      { delay_days: 0,  subject: 'Your renewal is 30 days out', body: 'Hi {first_name}, your policy renews soon. Want to schedule 10 minutes to confirm everything still fits — or quote alternatives if your situation\'s changed?' },
      { delay_days: 7,  subject: 'Renewal options for you', body: 'Hi {first_name}, just following up. I\'ve looked at a few options that may save you money. Worth a quick call?' },
      { delay_days: 14, subject: 'Locking in your renewal', body: 'Hi {first_name}, last call before I lock in your current plan as-is. Reply YES to confirm OR let\'s talk options.' },
    ],
  },
  birthday: {
    name: 'Birthday touch',
    description: 'Annual birthday greeting (warm, non-sales).',
    trigger_kind: 'birthday',
    steps: [
      { delay_days: 0, subject: 'Happy birthday, {first_name}!', body: 'Hi {first_name}, just a quick note — hope you have a great birthday!\n\n— {agent_name}' },
    ],
  },
};

export default function EmailDripsView({ currentUser, showToast = () => {} }) {
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab]       = React.useState('sequences'); // sequences | enrollments
  const [editing, setEditing] = React.useState(null);      // null | sequence object
  const [enrolling, setEnrolling] = React.useState(null);  // sequence id

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch('/api/email/drips')); }
    catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [showToast]);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading email drips…</div>;
  if (!data)   return null;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-emerald-900 text-white rounded-xl p-5">
        <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Email · Drip Sequences</div>
        <h1 className="text-xl font-bold mt-1">Recurring email campaigns</h1>
        <p className="text-slate-300 text-sm mt-2">Welcome series, 30-day check-ins, renewal nudges, birthdays. Each sequence is N steps with a delay-in-days per step. Tick fires every 15 min and respects RESEND_API_KEY for delivery (dev mode logs without sending).</p>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          <TabBtn active={tab==='sequences'} onClick={()=>setTab('sequences')}>Sequences ({data.sequences.length})</TabBtn>
          <TabBtn active={tab==='enrollments'} onClick={()=>setTab('enrollments')}>Active enrollments ({data.active_enrollments.filter(e => e.status === 'active').length})</TabBtn>
        </div>
        {tab === 'sequences' && (
          <div className="flex gap-1">
            {Object.keys(TEMPLATES).map(k => (
              <button key={k} onClick={() => setEditing({ ...TEMPLATES[k], id: null, enabled: true, dedupe_window_days: 90 })}
                className="text-xs font-bold uppercase tracking-wider bg-white border border-slate-200 hover:border-slate-400 text-slate-700 rounded-md px-2.5 py-1.5">
                + {TEMPLATES[k].name}
              </button>
            ))}
            <button onClick={() => setEditing({ name: '', trigger_kind: 'manual', steps: [{ delay_days: 0, subject: '', body: '' }], enabled: true, dedupe_window_days: 90 })}
              className="text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 inline-flex items-center gap-1">
              <Plus size={11}/> Blank
            </button>
          </div>
        )}
      </div>

      {tab === 'sequences' && !editing && (
        <SequenceList sequences={data.sequences} onEdit={setEditing} onEnroll={(id) => setEnrolling(id)} onChanged={load} showToast={showToast}/>
      )}
      {tab === 'sequences' && editing && (
        <SequenceEditor sequence={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} showToast={showToast}/>
      )}
      {tab === 'enrollments' && (
        <EnrollmentList enrollments={data.active_enrollments} sequences={data.sequences} onChanged={load} showToast={showToast}/>
      )}

      {enrolling && (
        <EnrollModal sequenceId={enrolling} onClose={() => setEnrolling(null)} onEnrolled={() => { setEnrolling(null); load(); }} showToast={showToast}/>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${active ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>{children}</button>
  );
}

function SequenceList({ sequences, onEdit, onEnroll, onChanged, showToast }) {
  const del = async (id) => {
    if (!window.confirm('Delete this sequence? Active enrollments will be cancelled.')) return;
    try {
      await apiFetch('/api/email/drips', { method: 'POST', body: JSON.stringify({ action: 'delete_sequence', id }) });
      onChanged();
    } catch (err) { showToast(`Delete failed: ${err.message}`); }
  };
  if (sequences.length === 0) {
    return <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500">No sequences yet. Click a template above to start.</div>;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
      {sequences.map(s => (
        <div key={s.id} className="px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-slate-900">{s.name}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{s.trigger_kind}</span>
            <span className="text-[10px] text-slate-500">{(s.steps || []).length} step{s.steps?.length === 1 ? '' : 's'}</span>
            {!s.enabled && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Disabled</span>}
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => onEnroll(s.id)} className="text-[10px] font-bold uppercase text-emerald-700 hover:text-emerald-900 px-2 py-1 border border-emerald-200 rounded inline-flex items-center gap-1"><Send size={10}/> Enroll</button>
              <button onClick={() => onEdit(s)} className="text-[10px] font-bold uppercase text-slate-700 hover:text-slate-900 px-2 py-1 border border-slate-200 rounded">Edit</button>
              <button onClick={() => del(s.id)} className="text-slate-400 hover:text-rose-700"><Trash2 size={12}/></button>
            </div>
          </div>
          {s.description && <div className="text-xs text-slate-500 mt-1">{s.description}</div>}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-slate-600">
            {(s.steps || []).map((st, i) => (
              <span key={i} className="bg-slate-50 border border-slate-200 rounded px-2 py-0.5">
                Step {i + 1}: {i === 0 ? `+${st.delay_days || 0}d from enrollment` : `+${st.delay_days || 0}d after step ${i}`} — “{(st.subject || '').slice(0, 40)}”
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SequenceEditor({ sequence, onClose, onSaved, showToast }) {
  const [s, setS] = React.useState(sequence);
  const [busy, setBusy] = React.useState(false);

  const updateStep = (i, patch) => {
    const steps = [...(s.steps || [])];
    steps[i] = { ...steps[i], ...patch };
    setS({ ...s, steps });
  };
  const addStep = () => setS({ ...s, steps: [...(s.steps || []), { delay_days: 7, subject: '', body: '' }] });
  const removeStep = (i) => setS({ ...s, steps: (s.steps || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!s.name.trim()) { showToast('Name required.'); return; }
    if ((s.steps || []).length === 0) { showToast('At least one step required.'); return; }
    setBusy(true);
    try {
      await apiFetch('/api/email/drips', { method: 'POST', body: JSON.stringify({ action: 'save_sequence', ...s }) });
      showToast('Sequence saved.');
      onSaved();
    } catch (err) { showToast(`Save failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border-2 border-emerald-300 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">{s.id ? 'Edit sequence' : 'New sequence'}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input value={s.name || ''} onChange={e => setS({ ...s, name: e.target.value })} placeholder="Sequence name" className="md:col-span-2 text-sm border border-slate-300 rounded-md px-3 py-2"/>
        <select value={s.trigger_kind || 'manual'} onChange={e => setS({ ...s, trigger_kind: e.target.value })} className="text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
          {TRIGGER_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <input value={s.description || ''} onChange={e => setS({ ...s, description: e.target.value })} placeholder="Description (optional)" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>

      <div className="space-y-2">
        {(s.steps || []).map((st, i) => (
          <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Step {i + 1}</span>
              <span className="text-xs text-slate-600">Send after</span>
              <input type="number" min="0" value={st.delay_days || 0} onChange={e => updateStep(i, { delay_days: Number(e.target.value) || 0 })} className="w-20 text-sm border border-slate-300 rounded-md px-2 py-1"/>
              <span className="text-xs text-slate-600">day(s) {i === 0 ? 'from enrollment' : `after step ${i}`}</span>
              <button onClick={() => removeStep(i)} className="ml-auto text-slate-400 hover:text-rose-700"><Trash2 size={12}/></button>
            </div>
            <input value={st.subject || ''} onChange={e => updateStep(i, { subject: e.target.value })} placeholder="Subject" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 mb-2"/>
            <textarea value={st.body || ''} onChange={e => updateStep(i, { body: e.target.value })} rows={5} placeholder="Body — merge tokens: {first_name} {agent_name} {company}" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          </div>
        ))}
        <button onClick={addStep} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={11}/> Add step</button>
      </div>

      <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
        <input type="checkbox" checked={s.enabled !== false} onChange={e => setS({ ...s, enabled: e.target.checked })}/>
        Enabled (off = no new enrollments, no ticks)
      </label>

      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md">{busy ? 'Saving…' : 'Save sequence'}</button>
        <button onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

function EnrollModal({ sequenceId, onClose, onEnrolled, showToast }) {
  const [email, setEmail] = React.useState('');
  const [name, setName]   = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [busy, setBusy]   = React.useState(false);

  const enroll = async () => {
    if (!email.trim()) { showToast('Email required.'); return; }
    setBusy(true);
    try {
      const r = await apiFetch('/api/email/drips', {
        method: 'POST',
        body: JSON.stringify({
          action: 'enroll',
          sequence_id: sequenceId,
          recipient_email: email,
          recipient_name: name,
          merge_context: { first_name: firstName || name.split(' ')[0] || '' },
        }),
      });
      if (r.deduped) showToast('Already enrolled (deduped).');
      else           showToast('Enrolled.');
      onEnrolled();
    } catch (err) { showToast(`Enroll failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Enroll recipient</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <div className="space-y-2">
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" autoFocus className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name (optional)" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
          <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name for {first_name} merge (defaults to first word of name)" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
          <div className="flex gap-2 pt-2">
            <button onClick={enroll} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md">{busy ? 'Enrolling…' : 'Enroll'}</button>
            <button onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EnrollmentList({ enrollments, sequences, onChanged, showToast }) {
  const seqById = new Map(sequences.map(s => [s.id, s]));

  const act = async (id, action) => {
    try {
      await apiFetch('/api/email/drips', { method: 'POST', body: JSON.stringify({ action, id }) });
      onChanged();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  if (enrollments.length === 0) {
    return <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500">No enrollments yet. Enroll a recipient from the Sequences tab.</div>;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
      {enrollments.map(e => {
        const seq = seqById.get(e.sequence_id);
        const totalSteps = (seq?.steps || []).length;
        const tone = e.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                     e.status === 'paused' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                     e.status === 'completed' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                                                 'bg-slate-50 text-slate-700 border-slate-200';
        return (
          <div key={e.id} className="px-4 py-2 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-slate-900 text-sm">{e.recipient_name || e.recipient_email}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${tone}`}>{e.status}</span>
                <span className="text-[10px] text-slate-500">{seq?.name || '(deleted sequence)'} · step {e.current_step + 1}/{totalSteps}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {e.status === 'active' && e.next_send_at && `Next: ${new Date(e.next_send_at).toLocaleString()}`}
                {e.last_sent_at && ` · last sent ${new Date(e.last_sent_at).toLocaleDateString()}`}
                {e.last_error && <span className="text-rose-700"> · {e.last_error}</span>}
              </div>
            </div>
            <div className="flex gap-1">
              {e.status === 'active' && <button onClick={() => act(e.id, 'pause')} className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900 px-2 py-1 border border-amber-200 rounded inline-flex items-center gap-1"><Pause size={10}/> Pause</button>}
              {e.status === 'paused' && <button onClick={() => act(e.id, 'resume')} className="text-[10px] font-bold uppercase text-emerald-700 hover:text-emerald-900 px-2 py-1 border border-emerald-200 rounded inline-flex items-center gap-1"><Play size={10}/> Resume</button>}
              {(e.status === 'active' || e.status === 'paused') && <button onClick={() => act(e.id, 'cancel')} className="text-[10px] font-bold uppercase text-rose-700 hover:text-rose-900 px-2 py-1 border border-rose-200 rounded">Cancel</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
